import { useQuery } from '@apollo/client'
import { AppBar, Button, Typography, useTheme } from '@material-ui/core'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import { WALLET_QUERY } from '../../graphql/wallet'
import { PROFORMA_QUERY } from '../../graphql/walletProforma'
import { getTranslate } from '../../localization'
import { Wallet } from '../../types/wallet'
import { Proforma } from '../../types/walletProforma'
import PaymentMethod from './PaymentMethod'
import ProformaStatusStepper from './ProformaStatusStepper'
import SummeryOfPayment from './SummeryOfPayment'
import TransactionList from './TransactionListTable'
import useStyles from './styles/index.styles'

const WalletProformaDetails = () => {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{ walletId: string; proformaId: string }>()
  const [attachments, setattachments] = useState<string[]>([])
  const [onlinePaymentSelected, setonlinePaymentSelected] = useState(true)

  const { data: wallet, loading: loadingForwallet } = useQuery<{
    wallet: Wallet
  }>(WALLET_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    }
  })

  const {
    data: proforma,
    loading: loadingForProformas,
    refetch
  } = useQuery<{
    proforma: Proforma
  }>(PROFORMA_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      proformaID: params.proformaId
    }
  })
  const walletName =
    wallet?.wallet.entity.entityType +
    '-' +
    wallet?.wallet.entity.entityName +
    '-' +
    wallet?.wallet.walletType +
    '-' +
    wallet?.wallet.currency
  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Wallet List'),
        link: `/adopay/wallets`
      },
      {
        label: walletName,
        link: `/adopay/wallet/${params.walletId}/details`
      },
      {
        label: getTranslate('Invoice List'),
        link: `/adopay/wallet/${params.walletId}/invoice-list`
      },
      {
        label: proforma?.proforma.proformaNumber || ''
      }
    ],
    [walletName]
  )

  return !loadingForProformas && proforma ? (
    <div>
      <PageHeader title={getTranslate('Invoice Details')} />
      <div className={classes.root}>
        <AppBar
          className={classes.appBar}
          position="static"
          color="transparent">
          <Breadcrumbs
            levels={cachedLevels}
            loading={loadingForProformas || loadingForwallet}
          />
        </AppBar>
      </div>
      <div className={classes.headerAndButtons}>
        <div className={classes.header}>
          <Typography variant="h3" gutterBottom>
            {getTranslate('Invoice Proposal')}
          </Typography>
          <Button
            endIcon={<OpenInNewIcon />}
            variant="contained"
            color="primary"
            disabled={loadingForProformas}
            onClick={() => {}}>
            {getTranslate('Export')}
          </Button>
        </div>
      </div>
      <div className={classes.detailsContainer}>
        <div>
          <div className={classes.transactionsListContainer}>
            <TransactionList
              transactions={proforma?.proforma.transactions || []}
              currency={wallet?.wallet.currency}
            />
          </div>
          <div className={classes.paymentMethodContainer}>
            <PaymentMethod
              disabled={proforma.proforma.status !== 'pendingPayment'}
              proforma={proforma?.proforma}
              {...{
                attachments,
                setattachments,
                onlinePaymentSelected,
                setonlinePaymentSelected
              }}
            />
          </div>
        </div>
        <div>
          <div className={classes.summeryOfPaymentContainer}>
            {wallet && (
              <SummeryOfPayment
                attachments={attachments}
                proforma={proforma?.proforma}
                wallet={wallet?.wallet}
                isOnlinePayment={onlinePaymentSelected}
                refetch={refetch}
                creditorName={
                  proforma.proforma.transactions[0].creditorEntity?.entityType +
                  ' - ' +
                  proforma.proforma.transactions[0].creditorEntity?.entityName
                }
              />
            )}
          </div>
          <div className={classes.proformaStatusContainer}>
            <ProformaStatusStepper
              proforma={proforma?.proforma}
              isOnlinePayment={onlinePaymentSelected}
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <AdotelSkeleton
      open={loadingForProformas}
      circlesColor={theme.palette.primary.main}
      numberOfCircles={4}
    />
  )
}

export default WalletProformaDetails
